import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faArrowDownToLine,
  faPrint,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Link } from 'react-router-dom';
import { Toaster, toast } from 'sonner';

import { LoadingAnimation } from '@/common-components/animations/loading/LoadingAnimation';
import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import { claimAttachmentIdAtom } from '@/modules/claims/summary/atoms';
import {
  PageProvider,
  usePageContext,
} from '@/modules/claims/summary/context/PageContext';
import { useClaimSummary } from '@/modules/claims/summary/hooks/useClaimSummary';
import { useExport } from '@/modules/claims/summary/hooks/useExport';
import { useTreatmentsToUse } from '@/modules/claims/summary/hooks/useTreatmentsToUse';
import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';
import { useGetNarrative } from '@/react-query/rcm/useNarrative';
import { useGeneratePdf } from '@/react-query/useGeneratePdf';
import { usePreferences } from '@/react-query/usePreferences';
import routesConstants from '@/routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ZoomToolTopbar } from '../../../../common-components/elements/ZoomToolTopbar.tsx/ZoomToolTopbar';
import {
  filterImagesByTreatments,
  generateFmxTitle,
  getEnabledAttachmentViews,
  getUniqueClaimNotesForSelectedTxs,
  sortImagesByDisplayItem,
  transformFmxImagesForPdf,
  transformToothLevelImagesForPdf,
} from '../../helpers';
import {
  claimAttachmentFmxImagesAtom,
  claimAttachmentFmxTableDataAtom,
  claimAttachmentToothLevelImagesAtom,
  claimNotesAtom,
  cleanClaimsAttachmentValuesAtom,
  clinicalNotesAtom,
  insPagesAtom,
  isNarrativeLoadingAtom,
  narrativeAtom,
  narrativeDevToolDataAtom,
  narrativeTraceIdAtom,
  selectedTreatmentCodesAtom,
  selectedTreatmentsAtom,
} from '../../summary/state';

import { ClaimAttachmentContent } from './claimAttachment/ClaimAttachmentContent';
import { ClaimCustomizationSidebar } from './claimCustomization/ClaimCustomizationSidebar';
import { NarrativeDevTool } from './NarrativeDevTool';

const RATE_ATTACHMENT_OPTIONS_POSITIVE = [
  {
    value: 'accurate-information',
    label: <Trans>Accurate information</Trans>,
  },
  {
    value: 'minimal-edits',
    label: <Trans>Minimal edits needed</Trans>,
  },
  {
    value: 'saved-time',
    label: <Trans>Saved time</Trans>,
  },
  {
    value: 'correct-terminology',
    label: <Trans>Correct terminology</Trans>,
  },
  {
    value: 'correct-images',
    label: <Trans>Correct images selected</Trans>,
  },
];

const RATE_ATTACHMENT_OPTIONS_NEGATIVE = [
  {
    value: 'missing-treatments',
    label: <Trans>Missing treatments</Trans>,
  },
  {
    value: 'incorrect-codes',
    label: <Trans>Incorrect codes</Trans>,
  },
  {
    value: 'mislabeled-images',
    label: <Trans>Mislabeled images</Trans>,
  },
  {
    value: 'incorrect-info',
    label: <Trans>Incorrect patient or provider information</Trans>,
  },
];

// Create a wrapper component that provides the context
const ClaimSummaryPageWrapper = () => {
  return (
    <PageProvider>
      <ClaimSummaryPage />
    </PageProvider>
  );
};

const ClaimSummaryPage = () => {
  // refs
  const contentRef = useRef<HTMLDivElement>(null);
  const inlineFeedbackBoxContainerRef = useRef<HTMLDivElement>(null);

  // atoms
  const [insPage, setInsPage] = useAtom(insPagesAtom);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useAtom(
    selectedTreatmentsAtom,
  );

  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const selectedTreatmentCodes = useAtomValue(selectedTreatmentCodesAtom);

  const setNarrative = useSetAtom(narrativeAtom);
  const setNarrativeDevToolData = useSetAtom(narrativeDevToolDataAtom);
  const setNarrativeTraceId = useSetAtom(narrativeTraceIdAtom);
  const setIsNarrativeLoading = useSetAtom(isNarrativeLoadingAtom);
  const setClaimAttachmentId = useSetAtom(claimAttachmentIdAtom);
  const setSelectedTreatmentCodes = useSetAtom(selectedTreatmentCodesAtom);
  const setClaimNotes = useSetAtom(claimNotesAtom);

  // state
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);
  const [hasInitiallyScaled, setHasInitiallyScaled] = useState(false);
  const [isPrintMode, setIsPrintMode] = useState(false);
  // hooks
  const { data: preferences } = usePreferences();
  const { unifiedId, estimatedVisitId = '' } = cleanClaimsAttachmentValues;
  const { t } = useLingui();
  const { currentPage, componentRef } = usePageContext();
  const { claimSummary, isLoadingClaimSummary } = useClaimSummary({
    estimatedVisitId: parseInt(estimatedVisitId),
    unifiedAppointmentId: parseInt(unifiedId),
  });
  const { trackEvent } = useTrackEvent();
  const hideHuddle = useShouldHideHuddle();
  const { handleExport: originalHandleExport } = useExport({
    patientName: cleanClaimsAttachmentValues.patientInfo.name,
    page: currentPage,
    componentRef,
    isPrintMode,
  });
  const isPrintVersionPdfEnabled = preferences?.claimAttachmentPdfEnabled;

  const handleAttachmentButtonClick = (rating: 'positive' | 'negative') => {
    // Only track positive ratings immediately - negative ratings will be tracked when feedback is submitted
    if (rating === 'positive') {
      trackEvent(mixpanelEvents.rateClaimAttachment, {
        txCode: selectedTreatmentCodes,
        source: 'attachment',
        rating,
      });
    }
  };

  const handleSubmitAttachmentFeedback = (
    rating: 'positive' | 'negative',
    selectedOptions: string[],
    additionalComments?: string,
  ) => {
    // TODO BOSON-319: Send rating to UserPilot

    // Track negative feedback when submitted (positive feedback was already tracked on button click)
    if (rating === 'negative') {
      trackEvent(mixpanelEvents.rateClaimAttachment, {
        txCode: selectedTreatmentCodes,
        source: 'attachment',
        rating,
        reason: selectedOptions.join(', ') || undefined,
        comment: additionalComments || undefined,
      });
    }
  };

  const handleExport = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, type: string) => {
      const oldScale = scale;
      setScale(1);
      // Wait for scale to update
      setTimeout(() => {
        originalHandleExport(e, type);
        // Restore original scale
        setScale(oldScale);
      }, 100);
    },
    [scale, setScale, originalHandleExport],
  );

  const { isLoading: isLoadingNarrative, isFetching: isFetchingNarrative } =
    useGetNarrative(
      parseInt(estimatedVisitId),
      selectedTreatmentIds?.map((tx) => Number(tx)) ?? [],
      parseInt(unifiedId),
      {
        onError() {
          setNarrative('None provided.');
          setNarrativeDevToolData(undefined);
          setNarrativeTraceId(undefined);
          setIsNarrativeLoading(false);
        },
        onSuccess(data) {
          setNarrative(data.narrative);
          setNarrativeDevToolData(data.devToolData);
          setNarrativeTraceId(data.traceId);
          setIsNarrativeLoading(false);
        },
        retry: true,
        enabled: (selectedTreatmentIds?.length ?? 0) > 0,
      },
    );

  // Atoms for Print Version button
  const {
    mutate: generatePdf,
    isLoading: isPdfLoading,
    reset: resetPdfMutation,
  } = useGeneratePdf();
  const images = useAtomValue(claimAttachmentToothLevelImagesAtom);
  const fmxImages = useAtomValue(claimAttachmentFmxImagesAtom);
  const fmxTableData = useAtomValue(claimAttachmentFmxTableDataAtom);
  const clinicalNote = useAtomValue(clinicalNotesAtom);
  const narrative = useAtomValue(narrativeAtom);
  const attachmentID = useAtomValue(claimAttachmentIdAtom);

  // Get original images for PDF generation
  const { colorImages: filteredColorImages, radiographs: filteredRadiographs } =
    useMemo(() => {
      const filteredImages = filterImagesByTreatments(
        claimSummary?.images ?? [],
        claimSummary?.imagesDisplay ?? [],
        selectedTreatmentIds,
      );

      const colorImages = filteredImages?.filter(
        (img) => img.imageType === 'COLOR_IMAGE',
      );

      const radiographs = filteredImages?.filter(
        (img) => img.imageType !== 'COLOR_IMAGE',
      );

      return {
        colorImages: sortImagesByDisplayItem(
          colorImages,
          claimSummary?.imagesDisplay ?? [],
        ),
        radiographs: sortImagesByDisplayItem(
          radiographs,
          claimSummary?.imagesDisplay ?? [],
        ),
      };
    }, [
      claimSummary?.images,
      claimSummary?.imagesDisplay,
      selectedTreatmentIds,
    ]);

  // Generate FMX title
  const fmxTitle = useMemo(() => {
    return generateFmxTitle(claimSummary?.treatments, selectedTreatmentIds);
  }, [claimSummary?.treatments, selectedTreatmentIds]);

  // variables
  const { treatments: claimSummaryTreatments = [] } = claimSummary ?? {};

  const { treatmentsToUse } = useTreatmentsToUse({
    allTreatments: claimSummaryTreatments ?? [],
    selectedTreatmentIds: selectedTreatmentIds ?? [],
  });

  const { hasToothLevelSummary, hasQuadLevelSummary } =
    getEnabledAttachmentViews({
      selectedTreatmentIds,
      claimSummary,
    });

  const loadingMessages = [
    <Trans>Selecting high quality radiographs...</Trans>,
    <Trans>Extracting clinical findings...</Trans>,
    <Trans>Crafting narrative...</Trans>,
  ];

  // handlers
  const handlePrintModeToggle = useCallback(
    (checked: boolean) => {
      setIsPrintMode(checked);
      trackEvent(mixpanelEvents.toggleAttachmentPrint, {
        unifiedId,
        estimatedVisitId,
        txCode: selectedTreatmentCodes,
      });
    },
    [
      setIsPrintMode,
      trackEvent,
      selectedTreatmentCodes,
      unifiedId,
      estimatedVisitId,
    ],
  );

  const handleSelectTreatments = useCallback(
    (txIds: string[]) => {
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(
          treatmentsToUse
            .filter((tx) => txIds.includes(tx.txId.toString()))
            .map((tx) => tx.txCode),
        ),
      ]);
    },
    [setSelectedTreatmentIds, setSelectedTreatmentCodes, treatmentsToUse],
  );

  // Transform data for PDF generation
  const transformedToothLevelImagesForPdf = useMemo(() => {
    return transformToothLevelImagesForPdf(images);
  }, [images]);

  const transformedFmxImagesForPdf = useMemo(() => {
    return transformFmxImagesForPdf(
      claimSummary,
      selectedTreatmentIds,
      fmxImages,
    );
  }, [claimSummary, selectedTreatmentIds, fmxImages]);

  // Simplified PDF generation handler
  const handleGeneratePdf = useCallback(() => {
    // Show warning toast about potential wait time and store its ID
    const warningToastId = toast(
      <Toast
        variant="warning"
        title={t`Generating PDF...`}
        description={t`This may take up to a minute. Please wait while we process your request.`}
      />,
      {
        closeButton: true,
        position: 'top-right',
        style: { width: '460px' },
      },
    );

    generatePdf(
      {
        unifiedAppointmentId: unifiedId,
        attachmentID,
        narrative,
        clinicalNote,
        toothLevelImages: transformedToothLevelImagesForPdf,
        fmxImages: transformedFmxImagesForPdf,
        fmxTableData,
        fmxTitle,
      },
      {
        onSuccess: () => {
          // Dismiss the warning toast
          toast.dismiss(warningToastId);

          toast(
            <Toast
              variant="success"
              title={t`PDF generated successfully!`}
              description={t`Your claim attachment PDF has been downloaded.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          // Reset the mutation state to ensure button text resets
          resetPdfMutation();
        },
        onError: (error) => {
          console.error('Failed to generate PDF:', error);

          // Dismiss the warning toast
          toast.dismiss(warningToastId);

          toast(
            <Toast
              variant="error"
              title={t`PDF generation failed`}
              description={t`Failed to generate PDF. Please try again or contact support if the problem persists.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          // Reset the mutation state to ensure button text resets
          resetPdfMutation();
        },
      },
    );
  }, [
    unifiedId,
    attachmentID,
    narrative,
    clinicalNote,
    fmxTableData,
    fmxTitle,
    transformedFmxImagesForPdf,
    transformedToothLevelImagesForPdf,
    generatePdf,
    resetPdfMutation,
    t,
  ]);

  // Download button handler
  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      selectedTreatmentCodes.length &&
        trackEvent(
          isPrintMode
            ? mixpanelEvents.downloadAttachmentPrint
            : mixpanelEvents.downloadAttachment,
          {
            unifiedId,
            estimatedVisitId,
            txCode: selectedTreatmentCodes,
          },
        );
      handleExport(e, 'jpeg');
    },
    [
      selectedTreatmentCodes,
      trackEvent,
      isPrintMode,
      unifiedId,
      estimatedVisitId,
      handleExport,
    ],
  );

  // useEffects
  // Handle cleanup on mount/unmount
  useEffect(() => {
    // Cleanup on unmount
    return () => {
      setClaimAttachmentId(undefined);
      setIsNarrativeLoading(false);
      setSelectedTreatmentIds([]);
      setSelectedTreatmentCodes([]);
    };
  }, [
    setClaimAttachmentId,
    setIsNarrativeLoading,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
  ]);

  // Sync loading state with the hook
  useEffect(() => {
    if ((selectedTreatmentIds?.length ?? 0) > 0) {
      setIsNarrativeLoading(isLoadingNarrative || isFetchingNarrative);
    }
  }, [
    isLoadingNarrative,
    selectedTreatmentIds,
    isFetchingNarrative,
    setIsNarrativeLoading,
  ]);

  // Handle treatments and claim notes updates
  useEffect(() => {
    // Set selected treatments when data loads
    if (claimSummaryTreatments?.length && !selectedTreatmentIds?.length) {
      const txIds = claimSummaryTreatments.map((tx) => tx.txId.toString());
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(claimSummaryTreatments.map((tx) => tx.txCode)),
      ]);
    }

    // Update claim notes when treatments or summary changes
    if (
      claimSummary &&
      selectedTreatmentIds &&
      selectedTreatmentIds.length > 0
    ) {
      const notes = getUniqueClaimNotesForSelectedTxs(
        claimSummary,
        selectedTreatmentIds.map(Number),
      );
      setClaimNotes(notes);
    }

    // Update content height when content changes
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [
    claimSummaryTreatments,
    selectedTreatmentIds,
    claimSummary,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setClaimNotes,
    contentRef,
  ]);

  // Update content height when scale changes
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [scale, contentRef, componentRef]);

  // Update content height when selected treatments change
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [selectedTreatmentIds, contentRef, componentRef, scale]);

  return (
    <div className="vai-w-screen vai-overflow-hidden vai-flex vai-flex-col vai-h-full">
      <div className="vai-px-6 vai-py-3 vai-bg-white vai-border-b vai-text-primary vai-flex vai-justify-between vai-items-center">
        {insPage === 'dev-tool' ? (
          <Button onClick={() => setInsPage('preview-single')} variant="ghost">
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="vai-mr-2 vai-text-primary"
            />
            Back to Claim Attachment
          </Button>
        ) : (
          <>
            <Link
              to={routesConstants.CLAIMS}
              onClick={() => {
                selectedTreatmentCodes?.length &&
                  trackEvent(mixpanelEvents.closeAttachmentEditor, {
                    unifiedId,
                    estimatedVisitId,
                    entryPoint: 'Top link',
                    txCode: selectedTreatmentCodes,
                  });
              }}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="vai-mr-2 vai-text-primary"
              />
              {hideHuddle ? (
                <Trans>All Patients</Trans>
              ) : (
                <Trans>Dashboard</Trans>
              )}
            </Link>
            <div className="vai-flex vai-gap-2">
              <Button
                disabled={
                  isLoadingClaimSummary ||
                  isLoadingNarrative ||
                  isFetchingNarrative
                }
                title={t`Download`}
                onClick={handleDownload}
              >
                <FontAwesomeIcon icon={faArrowDownToLine} />
                {isPrintMode ? (
                  <Trans>Download Print Version</Trans>
                ) : (
                  <Trans>Download</Trans>
                )}
              </Button>
              {isPrintVersionPdfEnabled && (
                <Button
                  disabled={
                    isLoadingClaimSummary ||
                    isPdfLoading ||
                    isLoadingNarrative ||
                    isFetchingNarrative
                  }
                  title={t`Print Version`}
                  variant="outline"
                  onClick={() => {
                    handleGeneratePdf();
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} />
                  {isPdfLoading ? (
                    <Trans>Generating...</Trans>
                  ) : (
                    <Trans>Print Version</Trans>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      {isLoadingClaimSummary ? (
        <LoadingAnimation translatedMessages={loadingMessages} />
      ) : (
        <div className="vai-flex-1 vai-overflow-auto">
          {insPage === 'dev-tool' ? (
            <NarrativeDevTool />
          ) : (
            <div className="vai-flex vai-flex-row vai-justify-between vai-h-full vai-gap-4">
              <div className="vai-flex-1 vai-h-full vai-overflow-hidden">
                <div className="vai-flex vai-flex-col vai-h-full">
                  <div className="vai-sticky vai-z-10 vai-w-full">
                    {/* Print Version Toggle */}
                    {!isPrintVersionPdfEnabled && (
                      <div className="vai-absolute vai-left-4 vai-flex vai-items-center vai-gap-2 vai-h-full">
                        <Switch
                          checked={isPrintMode}
                          onCheckedChange={handlePrintModeToggle}
                          id="print-mode"
                          className="vai-ml-1"
                        />
                        <label
                          htmlFor="print-mode"
                          className="vai-text-sm vai-text-gray-700"
                        >
                          <span className="vai-hidden lg:vai-inline">
                            <Trans>Show Print Version</Trans>
                          </span>
                          <span className="lg:vai-hidden">
                            <FontAwesomeIcon
                              icon={faPrint}
                              title={t`Show Print Version`}
                            />
                          </span>
                        </label>
                      </div>
                    )}
                    {/* Zoom Tool Topbar */}
                    <div className="vai-flex vai-justify-center vai-w-full">
                      <ZoomToolTopbar
                        contentRef={contentRef}
                        componentRef={
                          componentRef as React.RefObject<HTMLDivElement>
                        }
                        scale={scale}
                        selectedTreatmentIds={selectedTreatmentIds}
                        claimSummary={claimSummary}
                        hasInitiallyScaled={hasInitiallyScaled}
                        onSetHasInitiallyScaled={setHasInitiallyScaled}
                        onSetScale={setScale}
                      />
                    </div>
                    {/* Rate this attachment section */}
                    <div className="vai-absolute vai-right-4 vai-top-0 vai-bottom-0 vai-flex vai-items-center">
                      {preferences?.isFeedbackWidgetEnabled && (
                        <FeedbackWidget
                          inlineFeedbackBoxClassName="vai-mb-4"
                          onRatingButtonClick={handleAttachmentButtonClick}
                          onRate={handleSubmitAttachmentFeedback}
                          label={<Trans>Rate this attachment</Trans>}
                          positiveOptions={RATE_ATTACHMENT_OPTIONS_POSITIVE}
                          negativeOptions={RATE_ATTACHMENT_OPTIONS_NEGATIVE}
                          inlineFeedbackBoxContainerRef={
                            inlineFeedbackBoxContainerRef
                          }
                        />
                      )}
                    </div>
                  </div>
                  {/* Inline feedback box container */}
                  <div className="vai-w-full vai-overflow-auto">
                    <div className="vai-px-4 vai-min-w-fit vai-min-h-fit">
                      <div
                        ref={inlineFeedbackBoxContainerRef}
                        className="vai-w-full vai-max-w-full"
                      />
                    </div>
                  </div>
                  {/* Content Container */}
                  <div className="vai-flex-1 vai-w-full vai-overflow-auto">
                    <div
                      ref={contentRef}
                      style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        height: `${contentHeight}px`,
                      }}
                    >
                      <div
                        className="vai-p-4 vai-pt-0 vai-min-w-fit"
                        ref={componentRef as React.RefObject<HTMLDivElement>}
                      >
                        {/* Claim Attachment */}
                        <ClaimAttachmentContent
                          claimSummary={claimSummary}
                          isLoadingClaimSummary={isLoadingClaimSummary}
                          hasToothLevelSummary={hasToothLevelSummary}
                          hasQuadLevelSummary={hasQuadLevelSummary}
                          isPrintMode={isPrintMode}
                          filteredColorImages={filteredColorImages}
                          filteredRadiographs={filteredRadiographs}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Claim Customization Sidebar */}
              <ClaimCustomizationSidebar
                defaultTreatments={claimSummaryTreatments}
                onSelectTreatments={handleSelectTreatments}
              />
            </div>
          )}
        </div>
      )}
      <Toaster />
    </div>
  );
};

// Export the wrapper component instead to include context provider
export { ClaimSummaryPageWrapper as ClaimSummaryPage };
